import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { OnDataOptions } from '@apollo/client/react/types/types'
import { gql } from '@apollo/client'
import { useLazyQuery, useSubscription as useSubscriptionCore } from '@apollo/client/react'
import { parse } from 'graphql'
import upperFirst from 'lodash/upperFirst'

const QUEUE_JOB_COMPLETED_SUBSCRIPTION = gql`
  subscription queueJobCompleted($filter: JSON) {
    queueJobCompleted(filter: $filter) {
      jobId
      featureKey
      status
      message
      resultPayload
    }
  }
`

type QueueJobCompletedData = {
  queueJobCompleted: {
    jobId: string
    featureKey: string
    status: string
    message?: string
    resultPayload?: string
  }
}

export type UseQueueTrackingResult<TResult> = {
  isRunning: boolean
  isChecking: boolean
  startTracking: (jobId: string) => void
  result: TResult | null
}

/**
 * Derives the GraphQL query name from a featureKey using convention.
 * e.g. 'product.recomputeAll' → 'getProductRecomputeAllJobStatus'
 */
const deriveQueryName = (featureKey: string): string => {
  const pascal = featureKey
    .split('.')
    .map((segment) => upperFirst(segment))
    .join('')
  return `get${pascal}JobStatus`
}

/**
 * Reusable hook for tracking long-running background queue jobs.
 *
 * Convention-over-configuration: pass only the featureKey. The hook derives
 * the GraphQL query name automatically and subscribes by featureKey so it's
 * ready before the mutation fires — no race condition.
 *
 * Query name convention: `get{PascalCase(featureKey)}JobStatus`
 * e.g. featureKey 'product.recomputeAll' → query 'getProductRecomputeAllJobStatus'
 *
 * @param featureKey - the QueueTrackingKeyEnum value for this feature
 */
export const useQueueTracking = <TResult = unknown>(featureKey: string): UseQueueTrackingResult<TResult> => {
  const queryName = useMemo(() => deriveQueryName(featureKey), [featureKey])

  const [isRunning, setIsRunning] = useState(false)
  const [result, setResult] = useState<TResult | null>(null)

  // Ref tracks the active jobId — avoids stale closure in onData callback
  const jobIdRef = useRef<string | null>(null)

  // Buffer the last subscription event — handles race condition where job completes
  // before startTracking() is called (mutation response still in flight)
  const lastEventRef = useRef<QueueJobCompletedData['queueJobCompleted'] | null>(null)

  const statusQuery = useMemo(() => parse(`query ${queryName} { ${queryName} }`), [queryName])

  const [executeStatusQuery, { loading: isChecking }] = useLazyQuery(statusQuery, { fetchPolicy: 'network-only' })

  // On mount, query for any existing running job
  useEffect(() => {
    const checkExistingJob = async () => {
      const { data } = await executeStatusQuery()
      const existingJobId = data?.[queryName]

      if (existingJobId && typeof existingJobId === 'string') {
        jobIdRef.current = existingJobId
        setIsRunning(true)
      }
    }

    checkExistingJob()
  }, [executeStatusQuery, queryName])

  const parseResultPayload = useCallback((payload?: string): TResult | null => {
    if (!payload) return null
    try {
      return JSON.parse(payload) as TResult
    } catch {
      return null
    }
  }, [])

  // Handle subscription data — uses ref to avoid stale closure
  const onSubscriptionData = useCallback(
    ({ data }: OnDataOptions<QueueJobCompletedData>) => {
      const event = data?.data?.queueJobCompleted
      if (!event) return

      // If we're not tracking yet, buffer the event — startTracking() will check it
      if (!jobIdRef.current) {
        lastEventRef.current = event
        return
      }

      // Ignore events not matching the tracked jobId
      if (event.jobId !== jobIdRef.current) return

      setResult(parseResultPayload(event.resultPayload))
      setIsRunning(false)
      jobIdRef.current = null
      lastEventRef.current = null
    },
    [parseResultPayload]
  )

  // Always subscribe filtered by featureKey — ready before mutation fires, no race condition
  useSubscriptionCore<QueueJobCompletedData>(QUEUE_JOB_COMPLETED_SUBSCRIPTION, {
    variables: { filter: { featureKey } },
    onData: onSubscriptionData,
    shouldResubscribe: true,
  })

  const startTracking = useCallback(
    (newJobId: string) => {
      // Check if the job already completed while we were waiting for the mutation response
      const buffered = lastEventRef.current
      if (buffered?.jobId === newJobId) {
        setResult(parseResultPayload(buffered.resultPayload))
        setIsRunning(false)
        jobIdRef.current = null
        lastEventRef.current = null
        return
      }

      jobIdRef.current = newJobId
      setIsRunning(true)
      setResult(null)
      lastEventRef.current = null
    },
    [parseResultPayload]
  )

  return { isRunning, isChecking, startTracking, result }
}
